'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';

export const REJECTED = 0;
export const ACCEPTED = 1;
export const NOT_SEEN = -1;

export type ConsentStatus = typeof ACCEPTED | typeof REJECTED | typeof NOT_SEEN;

export type ConsentType =
   | 'analytics'
   | 'ab_testing_and_personalization'
   | 'conversion_tracking'
   | 'marketing_automation'
   | 'remarketing'
   | 'user_feedback'
   | 'custom_consent';

export type Consents = {
   [type in ConsentType]: {
      status: number;
   };
};

interface PpmsType {
   cm: {
      api: {
         (
            command: 'getNewComplianceTypes',
            onFulfilled: (types: ConsentType[]) => void,
            onRejected?: (error: unknown) => void
         ): void;
         (
            command: 'setInitialComplianceSettings',
            settings: { consents: ConsentType[] } | ConsentType[],
            onFulfilled: () => void,
            onRejected?: (error: unknown) => void
         ): void;
         (
            command: 'getComplianceSettings',
            onFulfilled: (complianceSettings: { consents: Consents }) => void,
            onRejected?: (error: unknown) => void
         ): void;
         (
            command: 'setComplianceSettings',
            settings: { consents: Consents },
            onFulfilled: () => void,
            onRejected?: (error: unknown) => void
         ): void;
         (command: 'trackAgreeToAllClick'): void;
         (command: 'trackRejectAllClick'): void;
         (command: 'trackMainFormView'): void;
      };
   };
}

declare global {
   interface Window {
      ppms?: PpmsType;
   }
}

export function usePiwikConsent() {
   const [isReady, setIsReady] = useState(false);
   const [consentStatus, setConsentStatus] = useState<ConsentStatus | null>(null);

   useEffect(() => {
      async function initialize() {
         await initializeConsentSettings();
         const status = await getConsentStatus();
         setConsentStatus(status);
         setIsReady(true);
      }
      void initialize();
   }, []);

   const acceptAll = useCallback(async () => {
      await updateAllConsents(ACCEPTED);
      setConsentStatus(ACCEPTED);
   }, []);

   const rejectAll = useCallback(async () => {
      await updateAllConsents(REJECTED);
      setConsentStatus(REJECTED);
   }, []);

   return useMemo(
      () => ({ isReady, consentStatus, acceptAll, rejectAll }),
      [isReady, consentStatus, acceptAll, rejectAll]
   );
}

function initializeConsentSettings(): Promise<void> {
   return new Promise(resolve => {
      const ppms = piwik();
      if (!ppms) {
         resolve();
         return;
      }
      ppms.cm.api('getNewComplianceTypes', (newConsentTypes: ConsentType[]) => {
         if (newConsentTypes.length === 0) {
            resolve();
            return;
         }
         ppms.cm.api('setInitialComplianceSettings', { consents: newConsentTypes }, resolve);
      });
   });
}

function getConsentStatus(): Promise<ConsentStatus | null> {
   return new Promise(resolve => {
      const ppms = piwik();
      if (!ppms) {
         resolve(null);
         return;
      }
      ppms.cm.api('getComplianceSettings', (complianceSettings: { consents: Consents }) => {
         const savedConsents = complianceSettings.consents;
         for (const consentType in savedConsents) {
            const consent = savedConsents[consentType as ConsentType];
            if (consent.status === NOT_SEEN) {
               resolve(NOT_SEEN);
               return;
            }
         }
         for (const consentType in savedConsents) {
            const consent = savedConsents[consentType as ConsentType];
            if (consent.status === REJECTED) {
               resolve(REJECTED);
               return;
            }
         }
         resolve(ACCEPTED);
      });
   });
}

function updateAllConsents(status: ConsentStatus): Promise<void> {
   return new Promise(resolve => {
      const ppms = piwik();
      if (!ppms) {
         resolve();
         return;
      }
      ppms.cm.api('getComplianceSettings', (complianceSettings: { consents: Consents }) => {
         const consents = complianceSettings.consents;
         for (const consentType in consents) {
            consents[consentType as ConsentType].status = status;
         }
         ppms.cm.api('setComplianceSettings', { consents }, resolve);
      });
   });
}

function piwik(): PpmsType | null {
   if (typeof window === 'undefined') return null;
   return window?.ppms?.cm?.api ? window.ppms : null;
}
